import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  matchesAPI, leaguesAPI, sportsAPI, competitionsAPI,
  facilitiesAPI, matchFieldAPI,
} from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Calendar as CalendarIcon, MapPin, Building2,
  Clock, Euro, Check, Users, Eye, EyeOff, Repeat, Sparkles,
} from 'lucide-react';
import { PlacesSearch, PlaceCard } from '@/components/PlacesSearch';

// =============================================================================
// Helpers
// =============================================================================

const TIME_SLOTS = ['18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00', '22:30'];
const DAY_NAMES = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

const pad = (n) => String(n).padStart(2, '0');

const formatLocalDateTime = (date, time) => {
  if (!date || !time) return '';
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  return `${y}-${m}-${d}T${time}`;
};

const parseDateTimeString = (s) => {
  if (!s) return { date: null, time: '' };
  const [datePart, timePart] = s.split('T');
  if (!datePart) return { date: null, time: '' };
  const [y, m, d] = datePart.split('-').map(Number);
  return {
    date: new Date(y, m - 1, d),
    time: timePart ? timePart.slice(0, 5) : '',
  };
};

// Convert any backend datetime (ISO with Z or offset) to a local "YYYY-MM-DDTHH:mm"
// string suitable for parseDateTimeString and `<input type="datetime-local">`.
const toLocalDateTimeInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const tzOffsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 16);
};

const nextWeekday = (weekdayIndex) => {
  const today = new Date();
  const diff = (weekdayIndex - today.getDay() + 7) % 7 || 7;
  const d = new Date(today);
  d.setDate(today.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Monday-first, like the backend (Python weekday: Monday = 0).
const WEEKDAYS = [...DAY_NAMES.slice(1), DAY_NAMES[0]];
const toWeekday = (date) => (date.getDay() + 6) % 7;

const WEEKDAY_SHORT = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

// "21:00" from "20:00": by default the next match comes out when this one ends.
const plusOneHour = (time) => {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  return `${pad((h + 1) % 24)}:${pad(m)}`;
};

// Mirrors services/match_series.occurrence/release_at for play days: the next play day
// comes out on the match day at `time` (a time before the start means after midnight).
const nextPlayDay = (matchDate, weekdays) => {
  const d = new Date(matchDate);
  do d.setDate(d.getDate() + 1); while (!weekdays.includes(toWeekday(d)));
  return d;
};
const releaseMoment = (matchDate, time) => {
  const [h, m] = time.split(':').map(Number);
  const t = new Date(matchDate);
  t.setHours(h, m, 0, 0);
  if (t < matchDate) t.setDate(t.getDate() + 1);
  return t;
};

const formatDateLong = (date) => {
  if (!date) return '—';
  return `${DAY_NAMES[date.getDay()]} ${date.getDate()}/${pad(date.getMonth() + 1)}`;
};

const getGoogleMapsUrl = ({ name, address, lat, lng, map_url: mapUrl }) => {
  if (mapUrl) return mapUrl;
  if (lat !== null && lat !== undefined && lng !== null && lng !== undefined) {
    return `https://www.google.com/maps?q=${lat},${lng}`;
  }
  const query = [name, address].filter(Boolean).join(', ');
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : '';
};

// =============================================================================
// Sub-components
// =============================================================================

const Stepper = ({ step, onJump, isEditing }) => {
  const items = isEditing
    ? [
        { n: 1, label: 'Quando' },
        { n: 2, label: 'Dove' },
        { n: 3, label: 'Dettagli' },
      ]
    : [
        { n: 1, label: 'Quando' },
        { n: 2, label: 'Dove' },
        { n: 3, label: 'Chi & Setup' },
      ];
  return (
    <div className="flex min-w-0 items-center justify-between gap-1 mb-6">
      {items.map((item, idx) => {
        const isDone = step > item.n;
        const isActive = step === item.n;
        return (
          <React.Fragment key={item.n}>
            <button
              type="button"
              onClick={() => onJump(item.n)}
              className="flex shrink-0 items-center gap-2 group"
            >
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm shadow-sm transition-all ${
                  isActive
                    ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white scale-110'
                    : isDone
                      ? 'bg-emerald-500 text-white'
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                {isDone ? <Check className="h-4 w-4" /> : item.n}
              </div>
              <span
                className={`text-sm font-medium hidden sm:inline ${
                  isActive ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {item.label}
              </span>
            </button>
            {idx < items.length - 1 && (
              <div className={`min-w-2 flex-1 h-0.5 mx-1 sm:mx-2 ${step > item.n ? 'bg-emerald-300' : 'bg-muted'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const PreviewCard = ({
  formData, selectedDate, selectedTime, selectedPlace, league,
  competitionName, repeatCount,
}) => {
  const dt = selectedDate && selectedTime
    ? `${formatDateLong(selectedDate)} · ${selectedTime}`
    : 'Scegli data e ora';

  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        Anteprima
      </div>
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-br from-slate-900 to-slate-700 dark:from-slate-800 dark:to-slate-950 text-white p-4">
          <div className="text-xs uppercase tracking-wider opacity-70">{dt}</div>
          <div className="text-lg font-bold mt-1 line-clamp-2">
            {formData.title || 'Titolo partita'}
          </div>
          {selectedPlace?.name && (
            <div className="text-sm opacity-80 mt-1 flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              <span className="truncate">{selectedPlace.name}</span>
            </div>
          )}
          {!selectedPlace?.name && (
            <div className="text-sm opacity-60 mt-1 italic">📍 (scegli un campo)</div>
          )}
        </div>
        <CardContent className="pt-4 space-y-2 text-sm">
          {league && (
            <div className="flex justify-between text-muted-foreground">
              <span>Lega</span>
              <span className="font-medium text-foreground truncate ml-2">{league.name}</span>
            </div>
          )}
          {competitionName && (
            <div className="flex justify-between text-muted-foreground">
              <span>Competizione</span>
              <span className="font-medium text-foreground truncate ml-2">{competitionName}</span>
            </div>
          )}
          <div className="flex justify-between text-muted-foreground">
            <span>Titolari</span>
            <span className="font-medium text-foreground">{formData.max_participants}</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>Riserve</span>
            <span className="font-medium text-foreground">{formData.max_reserves}</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>Visibilità</span>
            <span className="font-medium text-foreground">
              {formData.is_public ? '🌍 Pubblica' : '🔒 Privata'}
            </span>
          </div>
          {repeatCount > 1 && (
            <div className="flex justify-between text-purple-600 dark:text-purple-400 font-medium pt-2 border-t border-border mt-2">
              <span className="flex items-center gap-1">
                <Repeat className="h-3.5 w-3.5" />
                Ripetuta
              </span>
              <span>{repeatCount === Infinity ? 'dopo ogni partita' : `${repeatCount} partite`}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// =============================================================================
// Main component
// =============================================================================

export const CreateMatchPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const isEditing = Boolean(id);

  // Wizard
  const [step, setStep] = useState(1);

  // Data
  const [loading, setLoading] = useState(false);
  const [sports, setSports] = useState([]);
  const [myLeagues, setMyLeagues] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [competitionTeams, setCompetitionTeams] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState(null);

  const [formData, setFormData] = useState({
    league_id: searchParams.get('league') || '',
    competition_id: searchParams.get('competition') || '',
    sport_id: '',
    title: '',
    date_time: '',
    location_name: '',
    location_address: '',
    location_lat: null,
    location_lng: null,
    location_map_url: '',
    max_participants: 10,
    max_reserves: 5,
    is_public: true,
    notes: '',
    team_a_id: '',
    team_b_id: '',
    payment_amount: '',
    payment_link: '',
    payment_notes: '',
    goalkeeper_rotation_enabled: false,
  });

  const [selectedPlace, setSelectedPlace] = useState(null);

  // Date/time wizard state (separated for nicer UX)
  const initial = parseDateTimeString(formData.date_time);
  const [selectedDate, setSelectedDate] = useState(initial.date);
  const [selectedTime, setSelectedTime] = useState(initial.time);

  // Repeat: only this match is created now; after each match the next play day
  // comes out on its own. times = null → until stopped from "Gestisci partita".
  const [repeat, setRepeat] = useState({
    enabled: searchParams.get('repeat') === '1', times: null, publish_time: '', weekdays: [],
  });
  const [adminsConfirmed, setAdminsConfirmed] = useState(false);
  const matchWeekday = selectedDate ? toWeekday(selectedDate) : null;
  // The match day is always a play day; by default the next one comes out when it ends.
  const playDays = matchWeekday === null ? repeat.weekdays : [...new Set([...repeat.weekdays, matchWeekday])];
  const publishTime = repeat.publish_time || plusOneHour(selectedTime);
  const nextRelease = useMemo(() => {
    if (!repeat.enabled || !selectedDate || !selectedTime || !publishTime) return null;
    const [h, m] = selectedTime.split(':').map(Number);
    const start = new Date(selectedDate);
    start.setHours(h, m, 0, 0);
    return { next: nextPlayDay(start, playDays), at: releaseMoment(start, publishTime) };
  }, [repeat.enabled, selectedDate, selectedTime, publishTime, playDays.join()]); // eslint-disable-line react-hooks/exhaustive-deps

  // Facility/field linking
  const [facilities, setFacilities] = useState([]);
  const [facilityFields, setFacilityFields] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState('');
  const [selectedFieldId, setSelectedFieldId] = useState('');
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotPrice, setSlotPrice] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  // Editing: the field this match already has booked, and the time it was booked for.
  const [linkedBooking, setLinkedBooking] = useState(null);

  // ===========================================================================
  // Effects + loaders
  // ===========================================================================

  const loadCompetitions = async (leagueId, preselectCompetitionId = null) => {
    if (!leagueId) {
      setCompetitions([]);
      setSelectedCompetition(null);
      setCompetitionTeams([]);
      return;
    }
    try {
      const compsRes = await competitionsAPI.getAll({ league_id: leagueId });
      const comps = compsRes.data || [];
      setCompetitions(comps);

      if (preselectCompetitionId) {
        const comp = comps.find((c) => c.competition_id === preselectCompetitionId);
        if (comp) {
          setSelectedCompetition(comp);
          setFormData((prev) => ({
            ...prev,
            competition_id: comp.competition_id,
            sport_id: comp.sport_id,
          }));
          if (comp.registration_mode === 'teams') {
            const teamsRes = await competitionsAPI.getTeams(comp.competition_id);
            setCompetitionTeams(teamsRes.data || []);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sportsRes, leaguesRes, facRes] = await Promise.all([
          sportsAPI.getAll(),
          leaguesAPI.getMyLeagues(),
          facilitiesAPI.getAll(),
        ]);
        setSports(sportsRes.data);
        // Matches inside a league are organized only by its admins.
        const organizable = leaguesRes.data.filter(
          (l) => user?.role === 'admin' || ['admin', 'manager'].includes(l.user_role),
        );
        setMyLeagues(organizable);
        setFacilities(facRes.data || []);

        if (!isEditing && searchParams.get('facility')) {
          await handleFacilityChange(searchParams.get('facility'), {
            list: facRes.data || [], fieldId: searchParams.get('field'),
          });
        }

        if (isEditing) {
          const matchRes = await matchesAPI.getById(id);
          const match = matchRes.data.match;
          setFormData((prev) => ({
            ...prev,
            league_id: match.league_id || '',
            competition_id: match.competition_id || '',
            sport_id: match.sport_id || '',
            title: match.title || '',
            date_time: toLocalDateTimeInput(match.date_time),
            location_name: match.location_name || '',
            location_address: match.location_address || '',
            location_map_url: match.location_map_url || '',
            max_participants: match.max_participants ?? 10,
            max_reserves: match.max_reserves ?? 5,
            is_public: match.is_public ?? true,
            notes: match.notes || '',
            team_a_id: match.team_a_id || '',
            team_b_id: match.team_b_id || '',
            payment_amount:
              match.payment_amount === null || match.payment_amount === undefined
                ? ''
                : String(match.payment_amount),
            payment_link: match.payment_link || '',
            payment_notes: match.payment_notes || '',
            goalkeeper_rotation_enabled: !!match.goalkeeper_rotation_enabled,
          }));
          if (match.date_time) {
            const parsed = parseDateTimeString(toLocalDateTimeInput(match.date_time));
            setSelectedDate(parsed.date);
            setSelectedTime(parsed.time);
          }
          setSelectedPlace({
            name: match.location_name,
            address: match.location_address,
            map_url: match.location_map_url,
            lat: null,
            lng: null,
          });
          if (match.league_id) {
            await loadCompetitions(match.league_id, match.competition_id);
          }
          if (match.facility_id) {
            await handleFacilityChange(match.facility_id, {
              list: facRes.data || [], fieldId: match.field_id, keepLocation: true,
              dateTime: toLocalDateTimeInput(match.date_time),
            });
          }
          if (match.booking_id) {
            setLinkedBooking({
              booking_id: match.booking_id,
              field_id: match.field_id,
              date_time: toLocalDateTimeInput(match.date_time),
            });
          }
        } else if (searchParams.get('league')) {
          const sl = organizable.find((l) => l.league_id === searchParams.get('league'));
          if (sl) {
            setFormData((prev) => ({ ...prev, sport_id: sl.sport_id }));
            await loadCompetitions(sl.league_id, searchParams.get('competition'));
          } else {
            setFormData((prev) => ({ ...prev, league_id: '', competition_id: '' }));
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, id, isEditing]);

  // Sync date/time → formData.date_time
  useEffect(() => {
    const composed = formatLocalDateTime(selectedDate, selectedTime);
    setFormData((prev) => ({ ...prev, date_time: composed }));
    setSelectedSlot(null);
    if (selectedFacilityId && selectedFieldId && composed) {
      fetchAvailability(selectedFacilityId, selectedFieldId, composed);
    }
    // Smart default: auto-fill title if empty
    if (!isEditing && selectedDate && selectedTime && !formData.title) {
      setFormData((prev) => ({
        ...prev,
        title: prev.title || `Partita ${DAY_NAMES[selectedDate.getDay()]}`,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedTime]);

  // ===========================================================================
  // Handlers
  // ===========================================================================

  const handlePlaceSelect = (place) => {
    setSelectedPlace(place);
    setFormData((prev) => ({
      ...prev,
      location_name: place.name,
      location_address: place.address,
      location_lat: place.lat,
      location_lng: place.lng,
      location_map_url: getGoogleMapsUrl(place),
    }));
  };

  const handleLeagueChange = (leagueId) => {
    if (!leagueId || leagueId === 'none') {
      setFormData({ ...formData, league_id: '', competition_id: '', team_a_id: '', team_b_id: '' });
      setSelectedCompetition(null);
      setCompetitionTeams([]);
      setCompetitions([]);
      return;
    }
    const league = myLeagues.find((l) => l.league_id === leagueId);
    setFormData({
      ...formData,
      league_id: leagueId,
      sport_id: league?.sport_id || formData.sport_id,
      competition_id: '',
      team_a_id: '',
      team_b_id: '',
    });
    setSelectedCompetition(null);
    setCompetitionTeams([]);
    loadCompetitions(leagueId);
  };

  const handleCompetitionChange = async (competitionId) => {
    if (!competitionId || competitionId === 'none') {
      setSelectedCompetition(null);
      setCompetitionTeams([]);
      setFormData((prev) => ({ ...prev, competition_id: '', team_a_id: '', team_b_id: '' }));
      return;
    }
    const comp = competitions.find((c) => c.competition_id === competitionId);
    setSelectedCompetition(comp || null);
    setFormData((prev) => ({
      ...prev,
      competition_id: competitionId,
      team_a_id: '',
      team_b_id: '',
      sport_id: comp?.sport_id || prev.sport_id,
    }));
    if (comp?.registration_mode === 'teams') {
      try {
        const teamsRes = await competitionsAPI.getTeams(competitionId);
        setCompetitionTeams(teamsRes.data || []);
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    } else {
      setCompetitionTeams([]);
    }
  };

  const handleFacilityChange = async (facilityId, {
    list = facilities, fieldId = '', keepLocation = false, dateTime = formData.date_time,
  } = {}) => {
    if (!facilityId || facilityId === 'none') {
      setSelectedFacilityId('');
      setSelectedFieldId('');
      setFacilityFields([]);
      setAvailabilitySlots([]);
      setSelectedSlot(null);
      setSlotPrice(null);
      return;
    }
    setSelectedFacilityId(facilityId);
    setSelectedFieldId(fieldId || '');
    setAvailabilitySlots([]);
    setSelectedSlot(null);
    setSlotPrice(null);
    try {
      const res = await facilitiesAPI.getFields(facilityId);
      setFacilityFields(res.data || []);
    } catch (error) {
      console.error('Error fetching facility fields:', error);
      setFacilityFields([]);
    }
    if (fieldId) fetchAvailability(facilityId, fieldId, dateTime);
    const fac = list.find((f) => f.facility_id === facilityId);
    if (fac && !keepLocation) {
      const mapUrl = getGoogleMapsUrl(fac);
      setFormData((prev) => ({
        ...prev,
        location_name: fac.name,
        location_address: fac.address || '',
        location_lat: fac.lat || null,
        location_lng: fac.lng || null,
        location_map_url: mapUrl,
      }));
      setSelectedPlace({
        name: fac.name, address: fac.address,
        lat: fac.lat, lng: fac.lng, map_url: mapUrl,
      });
    }
  };

  const fetchAvailability = async (facilityId, fieldId, dateTime) => {
    if (!facilityId || !fieldId || !dateTime) {
      setAvailabilitySlots([]);
      setSelectedSlot(null);
      setSlotPrice(null);
      return;
    }
    const dateStr = dateTime.split('T')[0];
    if (!dateStr) return;
    setLoadingSlots(true);
    try {
      const res = await facilitiesAPI.getFieldAvailability(facilityId, fieldId, dateStr);
      const slots = res.data.slots || [];
      setAvailabilitySlots(slots);
      setSlotPrice(res.data.slot_price);
      // The booked slot is always the match time: pick it when it's free.
      const time = dateTime.split('T')[1]?.slice(0, 5);
      setSelectedSlot(slots.find((slot) => slot.available && slot.start === time) || null);
    } catch (error) {
      console.error('Error fetching availability:', error);
      setAvailabilitySlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleFieldChange = (fieldId) => {
    if (!fieldId || fieldId === 'none') {
      setSelectedFieldId('');
      setAvailabilitySlots([]);
      setSelectedSlot(null);
      return;
    }
    setSelectedFieldId(fieldId);
    setSelectedSlot(null);
    fetchAvailability(selectedFacilityId, fieldId, formData.date_time);
  };

  // Editing without touching field or time: the booking stays as it is.
  const keepsLinkedBooking = !!linkedBooking
    && linkedBooking.field_id === selectedFieldId
    && linkedBooking.date_time === formData.date_time;

  // ===========================================================================
  // Validation per step
  // ===========================================================================

  const validateStep = (s) => {
    if (s === 1) {
      if (!selectedDate || !selectedTime) {
        toast.error('Scegli data e ora');
        return false;
      }
    }
    if (s === 2) {
      if (!formData.location_name || !formData.sport_id) {
        toast.error('Manca lo sport o il luogo');
        return false;
      }
      if (fieldNeedsFreeSlot()) {
        toast.error(`Alle ${selectedTime} il campo è occupato: scegli un orario libero o togli il campo`);
        return false;
      }
    }
    return true;
  };

  function fieldNeedsFreeSlot() {
    const sameField = linkedBooking?.field_id === selectedFieldId;
    return !!selectedFieldId && !selectedSlot && availabilitySlots.length > 0 && !keepsLinkedBooking && !sameField;
  }

  const goNext = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(3, s + 1));
  };

  // ===========================================================================
  // Submit
  // ===========================================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.date_time || !formData.location_name || !formData.sport_id) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    if (selectedCompetition?.registration_mode === 'teams') {
      if (!formData.team_a_id || !formData.team_b_id) {
        toast.error('Seleziona entrambe le squadre');
        return;
      }
      if (formData.team_a_id === formData.team_b_id) {
        toast.error('Le squadre devono essere diverse');
        return;
      }
    }

    let paymentAmount = null;
    const rawAmount = String(formData.payment_amount ?? '').trim().replace(',', '.');
    if (rawAmount !== '') {
      const parsed = Number(rawAmount);
      if (!Number.isFinite(parsed) || parsed < 0) {
        toast.error('Importo pagamento non valido');
        return;
      }
      paymentAmount = parsed;
    }
    const paymentLink = (formData.payment_link || '').trim() || null;
    const paymentNotes = (formData.payment_notes || '').trim() || null;

    if (fieldNeedsFreeSlot()) {
      toast.error(`Alle ${selectedTime} il campo è occupato: scegli un orario libero o togli il campo`);
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        const payload = {
          title: formData.title,
          date_time: formData.date_time,
          location_name: formData.location_name,
          location_address: formData.location_address,
          location_map_url: formData.location_map_url,
          max_participants: formData.max_participants,
          max_reserves: formData.max_reserves,
          is_public: formData.is_public,
          notes: formData.notes,
          team_a_id: formData.team_a_id || null,
          team_b_id: formData.team_b_id || null,
          league_id: formData.league_id || null,
          competition_id: formData.competition_id || null,
          payment_amount: paymentAmount,
          payment_link: paymentLink,
          payment_notes: paymentNotes,
          goalkeeper_rotation_enabled: !!formData.goalkeeper_rotation_enabled,
        };
        const sameField = linkedBooking?.field_id === selectedFieldId;
        // A new or different field is booked first, so a taken slot stops the save
        // before anything changes. The same field just moves with the match (backend).
        if (selectedFieldId && !sameField) {
          if (selectedSlot) {
            await facilitiesAPI.createBooking(selectedFacilityId, {
              field_id: selectedFieldId,
              date: payload.date_time.split('T')[0],
              start_time: selectedSlot.start,
              end_time: selectedSlot.end,
              match_id: id,
              match_title: payload.title,
            });
          } else {
            await matchFieldAPI.assignField(id, { facility_id: selectedFacilityId, field_id: selectedFieldId });
          }
        }
        if (linkedBooking && !sameField) {
          await facilitiesAPI.cancelBooking(linkedBooking.booking_id);
        }
        await matchesAPI.update(id, payload);
        toast.success('Partita aggiornata!');
        navigate(`/matches/${id}`);
        return;
      }

      const payload = {
        ...formData,
        payment_amount: paymentAmount,
        payment_link: paymentLink,
        payment_notes: paymentNotes,
        admins_confirmed: adminsConfirmed,
        repeat: repeat.enabled ? {
          weekdays: playDays,
          count: repeat.times ? repeat.times + 1 : null,
          publish_time: publishTime,
        } : null,
      };
      const res = await matchesAPI.create(payload);
      const firstMatchId = res.data.match_id;
      // Field assignment / booking only for the first match: the others would
      // need their own slots.
      if (selectedFacilityId && selectedFieldId) {
        if (selectedSlot) {
          try {
            await facilitiesAPI.createBooking(selectedFacilityId, {
              field_id: selectedFieldId,
              date: payload.date_time.split('T')[0],
              start_time: selectedSlot.start,
              end_time: selectedSlot.end,
              match_id: firstMatchId,
              match_title: payload.title,
              league_id: payload.league_id || '',
              competition_id: payload.competition_id || '',
              notes: payload.notes || '',
            });
          } catch (err) {
            console.error('Booking error:', err);
            toast.warning('Partita creata, ma la prenotazione campo non è riuscita');
          }
        } else {
          try {
            await matchFieldAPI.assignField(firstMatchId, {
              facility_id: selectedFacilityId,
              field_id: selectedFieldId,
            });
          } catch (err) {
            console.error('Field assignment error:', err);
          }
        }
      }

      toast.success(
        repeat.enabled
          ? `Partita creata! Dopo ogni partita, alle ${publishTime}, esce da sola la prossima`
          : 'Partita creata con successo!',
      );
      navigate(`/matches/${firstMatchId}`);
    } catch (error) {
      toast.error(
        error.response?.data?.detail === 'Time slot already booked'
          ? 'Il campo è già prenotato a quell\'ora: scegli un altro orario'
          : error.response?.data?.detail || 'Errore nel salvataggio',
      );
    } finally {
      setLoading(false);
    }
  };

  // ===========================================================================
  // Derived
  // ===========================================================================

  const currentLeague = useMemo(
    () => myLeagues.find((l) => l.league_id === formData.league_id) || null,
    [myLeagues, formData.league_id],
  );

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <div className="min-w-0 overflow-x-hidden p-3 sm:p-4 md:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="create-match-page">
      <Button variant="ghost" className="mb-4" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Indietro
      </Button>

      <div className="mb-4 flex min-w-0 items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow">
          <CalendarIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold leading-tight">
            {isEditing ? 'Modifica Partita' : 'Crea Nuova Partita'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEditing ? 'Aggiorna dettagli e salva' : 'Tre passi e sei online'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 min-w-0">
          <Card className="min-w-0 overflow-hidden">
            <CardContent className="p-3 pt-5 sm:p-6 sm:pt-6">
              <Stepper step={step} onJump={(n) => setStep(n)} isEditing={isEditing} />

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* ============================================================ */}
                {/* STEP 1 — Quando                                              */}
                {/* ============================================================ */}
                {step === 1 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5 text-blue-600" />
                        Quando giochiamo?
                      </h2>
                    </div>

                    {/* Quick presets */}
                    <div>
                      <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                        Suggerimenti rapidi
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => { setSelectedDate(nextWeekday(1)); setSelectedTime('21:00'); }}
                          className="px-3 py-2 text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-900"
                        >
                          Lunedì 21:00
                        </button>
                        <button
                          type="button"
                          onClick={() => { setSelectedDate(nextWeekday(2)); setSelectedTime('20:00'); }}
                          className="px-3 py-2 text-sm font-medium bg-muted hover:bg-muted/70 rounded-lg"
                        >
                          Martedì 20:00
                        </button>
                        <button
                          type="button"
                          onClick={() => { setSelectedDate(nextWeekday(4)); setSelectedTime('20:00'); }}
                          className="px-3 py-2 text-sm font-medium bg-muted hover:bg-muted/70 rounded-lg"
                        >
                          Giovedì 20:00
                        </button>
                        <button
                          type="button"
                          onClick={() => { setSelectedDate(nextWeekday(5)); setSelectedTime('21:00'); }}
                          className="px-3 py-2 text-sm font-medium bg-muted hover:bg-muted/70 rounded-lg"
                        >
                          Venerdì 21:00
                        </button>
                      </div>
                    </div>

                    {/* Calendar + time slots */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label className="mb-2 block">Giorno</Label>
                        <div className="overflow-hidden border rounded-lg p-1.5 sm:p-2 bg-card">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(d) => d && setSelectedDate(d)}
                            disabled={false}
                            initialFocus
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="mb-2 block">Orario</Label>
                        <div className="grid grid-cols-3 gap-2">
                          {TIME_SLOTS.map((t) => (
                            <button
                              key={t}
                              type="button"
                              onClick={() => setSelectedTime(t)}
                              className={`px-2 py-2.5 text-sm font-medium rounded-lg border transition-all ${
                                selectedTime === t
                                  ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-transparent shadow'
                                  : 'bg-card hover:bg-accent border-border'
                              }`}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                        <details className="mt-3">
                          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                            Inserisci un orario diverso
                          </summary>
                          <Input
                            type="time"
                            value={selectedTime}
                            onChange={(e) => setSelectedTime(e.target.value)}
                            className="mt-2"
                          />
                        </details>
                      </div>
                    </div>

                    {/* Repeat: the next matches come out on their own */}
                    {!isEditing && (
                      <div className="p-4 rounded-lg border-2 border-dashed border-purple-300 dark:border-purple-900 bg-purple-50/50 dark:bg-purple-950/20">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex min-w-0 items-center gap-2">
                            <Repeat className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                            <Label className="font-semibold text-purple-900 dark:text-purple-200">
                              Ripeti automaticamente
                            </Label>
                          </div>
                          <Switch
                            className="shrink-0"
                            checked={repeat.enabled}
                            onCheckedChange={(v) => setRepeat((r) => ({ ...r, enabled: v }))}
                          />
                        </div>
                        <p className="text-xs text-purple-800/80 dark:text-purple-300/80">
                          Finita una partita esce da sola la prossima, nel giorno di gioco successivo, con il campo da decidere e la notifica alla lega.
                        </p>
                        {repeat.enabled && (
                          <div className="grid grid-cols-2 gap-3 mt-3">
                            <div className="col-span-2">
                              <Label className="text-xs">Giorni in cui giocate</Label>
                              <div className="flex gap-1.5 mt-1">
                                {WEEKDAY_SHORT.map((label, day) => {
                                  const on = playDays.includes(day);
                                  return (
                                    <button
                                      key={day}
                                      type="button"
                                      aria-label={WEEKDAYS[day]}
                                      aria-pressed={on}
                                      disabled={day === matchWeekday}
                                      onClick={() => setRepeat((r) => ({
                                        ...r,
                                        weekdays: on ? r.weekdays.filter((d) => d !== day) : [...r.weekdays, day],
                                      }))}
                                      className={`h-9 w-9 rounded-full text-sm font-semibold border transition-colors ${
                                        on
                                          ? 'bg-purple-600 text-white border-purple-600'
                                          : 'bg-background text-muted-foreground border-border'
                                      } disabled:opacity-100 disabled:cursor-default`}
                                    >
                                      {label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Quante volte ripetere</Label>
                              <Input
                                type="number"
                                min={1}
                                max={199}
                                placeholder="Sempre"
                                value={repeat.times ?? ''}
                                onChange={(e) => {
                                  const n = parseInt(e.target.value, 10);
                                  setRepeat((r) => ({ ...r, times: n > 0 ? Math.min(199, n) : null }));
                                }}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">A che ora esce</Label>
                              <Input
                                type="time"
                                value={publishTime}
                                onChange={(e) => setRepeat((r) => ({ ...r, publish_time: e.target.value }))}
                              />
                            </div>
                            {nextRelease && (
                              <p className="col-span-2 text-xs text-purple-800/80 dark:text-purple-300/80">
                                Es. la partita di {formatDateLong(nextRelease.next)} esce {formatDateLong(nextRelease.at)} alle {publishTime}
                                {!repeat.times ? ', e così via finché non la fermi.' : repeat.times > 1 ? `, poi altre ${repeat.times - 1}.` : '.'}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ============================================================ */}
                {/* STEP 2 — Dove                                                */}
                {/* ============================================================ */}
                {step === 2 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-emerald-600" />
                        Dove giochiamo?
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2 min-w-0">
                        <Label>Lega</Label>
                        <Select
                          value={formData.league_id || 'none'}
                          onValueChange={handleLeagueChange}
                        >
                          <SelectTrigger data-testid="match-league-select">
                            <SelectValue placeholder="Nessuna lega" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nessuna lega</SelectItem>
                            {myLeagues.map((league) => (
                              <SelectItem key={league.league_id} value={league.league_id}>
                                {league.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Sport *</Label>
                        <Select
                          value={formData.sport_id}
                          onValueChange={(v) => setFormData({ ...formData, sport_id: v })}
                          disabled={isEditing || !!selectedCompetition}
                        >
                          <SelectTrigger data-testid="match-sport-select">
                            <SelectValue placeholder="Seleziona sport" />
                          </SelectTrigger>
                          <SelectContent>
                            {sports.map((sport) => (
                              <SelectItem key={sport.sport_id} value={sport.sport_id}>
                                {sport.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {formData.league_id && (
                      <div className="space-y-2">
                        <Label>Competizione (opzionale)</Label>
                        <Select
                          value={formData.competition_id || 'none'}
                          onValueChange={handleCompetitionChange}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona competizione" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nessuna competizione</SelectItem>
                            {competitions.map((comp) => (
                              <SelectItem key={comp.competition_id} value={comp.competition_id}>
                                {comp.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedCompetition && (
                          <p className="text-xs text-muted-foreground">
                            {selectedCompetition.competition_type === 'tournament' ? 'Torneo' : 'Campionato'}
                            {' · '}
                            {selectedCompetition.registration_mode === 'teams' ? 'Squadre' : 'Giocatori'}
                          </p>
                        )}
                      </div>
                    )}

                    {selectedCompetition?.registration_mode === 'teams' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2 min-w-0">
                          <Label>Squadra A *</Label>
                          <Select
                            value={formData.team_a_id}
                            onValueChange={(v) => setFormData({ ...formData, team_a_id: v })}
                          >
                            <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                            <SelectContent>
                              {competitionTeams.map((t) => (
                                <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Squadra B *</Label>
                          <Select
                            value={formData.team_b_id}
                            onValueChange={(v) => setFormData({ ...formData, team_b_id: v })}
                          >
                            <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                            <SelectContent>
                              {competitionTeams.map((t) => (
                                <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}

                    {/* Facility / Field */}
                    {facilities.length > 0 && (
                      <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <Building2 className="h-4 w-4 text-primary" />
                          Prenota un campo su LeagueHub (opzionale)
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Select
                            value={selectedFacilityId || 'none'}
                            onValueChange={handleFacilityChange}
                          >
                            <SelectTrigger><SelectValue placeholder="Seleziona struttura" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nessuna struttura</SelectItem>
                              {facilities.map((f) => (
                                <SelectItem key={f.facility_id} value={f.facility_id}>
                                  {f.name} {f.city ? `(${f.city})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {selectedFacilityId && facilityFields.length > 0 && (
                            <Select
                              value={selectedFieldId || 'none'}
                              onValueChange={handleFieldChange}
                            >
                              <SelectTrigger><SelectValue placeholder="Seleziona campo" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Nessun campo</SelectItem>
                                {facilityFields.map((f) => (
                                  <SelectItem key={f.field_id} value={f.field_id}>
                                    {f.name} {f.hourly_rate ? `(€${f.hourly_rate}/h)` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>

                        {selectedFieldId && formData.date_time && (
                          <div className="space-y-2 pt-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              Slot disponibili
                              {slotPrice && (
                                <Badge variant="secondary" className="ml-auto">
                                  <Euro className="h-3 w-3 mr-1" />
                                  {slotPrice} / slot
                                </Badge>
                              )}
                            </div>
                            {loadingSlots ? (
                              <p className="text-xs text-muted-foreground">Caricamento...</p>
                            ) : availabilitySlots.length === 0 ? (
                              <p className="text-xs text-muted-foreground">Nessuno slot disponibile.</p>
                            ) : (
                              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                                {availabilitySlots.map((slot, i) => (
                                  <button
                                    key={i}
                                    type="button"
                                    disabled={!slot.available}
                                    onClick={() => setSelectedTime(slot.start)}
                                    className={`px-2 py-2 rounded-md text-xs font-medium border transition-all ${
                                      !slot.available
                                        ? 'bg-muted text-muted-foreground/50 cursor-not-allowed line-through'
                                        : selectedSlot?.start === slot.start
                                          ? 'bg-primary text-primary-foreground border-primary ring-2 ring-primary/30'
                                          : 'bg-background hover:bg-accent border-border cursor-pointer'
                                    }`}
                                  >
                                    {slot.start} - {slot.end}
                                    {selectedSlot?.start === slot.start && (
                                      <Check className="h-3 w-3 inline ml-1" />
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                            {!loadingSlots && availabilitySlots.length > 0 && (
                              <p className={`text-xs ${selectedSlot || keepsLinkedBooking ? 'text-muted-foreground' : 'text-destructive'}`}>
                                {keepsLinkedBooking
                                  ? `Campo prenotato per questa partita alle ${selectedTime}`
                                  : selectedSlot
                                    ? `Il campo viene prenotato alle ${selectedSlot.start}, l'orario della partita`
                                    : `Alle ${selectedTime} il campo è occupato: tocca un orario libero`}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Label>Cerca una struttura o un campo</Label>
                        <Button
                          type="button"
                          variant="link"
                          className="h-auto p-0 text-xs"
                          onClick={() => navigate('/facilities/new')}
                        >
                          Salva una nuova struttura
                        </Button>
                      </div>
                      <PlacesSearch
                        onSelect={handlePlaceSelect}
                        placeholder="Cerca campo, palestra, centro sportivo..."
                        initialValue={formData.location_name}
                      />
                      <p className="text-xs text-muted-foreground">
                        Seleziona un risultato e il link Google Maps viene compilato automaticamente. Per riutilizzarlo in futuro, salvalo come struttura.
                      </p>
                      {selectedPlace && (
                        <PlaceCard
                          name={selectedPlace.name}
                          address={selectedPlace.address}
                          lat={selectedPlace.lat}
                          lng={selectedPlace.lng}
                          mapUrl={selectedPlace.map_url}
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="location_address">Indirizzo per Google Maps</Label>
                      <Input
                        id="location_address"
                        placeholder="Es: Via Giuseppe Ungaretti 5, Cavernago BG, Italia"
                        value={formData.location_address}
                        onChange={(e) => {
                          const addr = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            location_address: addr,
                            location_map_url: addr
                              ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`
                              : prev.location_map_url,
                          }));
                        }}
                        data-testid="match-location-address-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Aggiungi numero civico, città e paese per indicazioni precise nei reminder
                        (es. "Via Roma 12, Cavernago BG, Italia").
                      </p>
                    </div>
                  </div>
                )}

                {/* ============================================================ */}
                {/* STEP 3 — Dettagli                                            */}
                {/* ============================================================ */}
                {step === 3 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Users className="h-5 w-5 text-purple-600" />
                        Chi gioca?
                      </h2>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="title">Titolo *</Label>
                      <Input
                        id="title"
                        placeholder="Es: Partita Serale - Giovedì"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        data-testid="match-title-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="max_participants">Titolari richiesti</Label>
                        <Input
                          id="max_participants"
                          type="number" min="2" max="50"
                          value={formData.max_participants}
                          onChange={(e) => setFormData({
                            ...formData,
                            max_participants: parseInt(e.target.value),
                          })}
                          data-testid="match-max-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Numero esatto di giocatori per la partita
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="max_reserves">Max riserve</Label>
                        <Input
                          id="max_reserves"
                          type="number" min="0" max="20"
                          value={formData.max_reserves}
                          onChange={(e) => setFormData({
                            ...formData,
                            max_reserves: parseInt(e.target.value),
                          })}
                          data-testid="match-reserves-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Subentrano automaticamente se un titolare lascia
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notes">Note</Label>
                      <Textarea
                        id="notes"
                        placeholder="Note per i partecipanti..."
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-3 p-4 rounded-lg border bg-muted/40">
                      <div className="flex items-center gap-2">
                        <Euro className="h-4 w-4 text-muted-foreground" />
                        <Label className="font-medium">Info pagamento (opzionale)</Label>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="payment_amount">Importo (€)</Label>
                          <Input
                            id="payment_amount"
                            type="number"
                            min="0"
                            step="0.5"
                            inputMode="decimal"
                            placeholder="es. 7.50"
                            value={formData.payment_amount}
                            onChange={(e) => setFormData({ ...formData, payment_amount: e.target.value })}
                            data-testid="match-payment-amount-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="payment_link">Link pagamento</Label>
                          <Input
                            id="payment_link"
                            type="url"
                            placeholder="https://paypal.me/..."
                            value={formData.payment_link}
                            onChange={(e) => setFormData({ ...formData, payment_link: e.target.value })}
                            data-testid="match-payment-link-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="payment_notes">Causale / note</Label>
                        <Input
                          id="payment_notes"
                          placeholder="es. 5€ campo + 1€ palloni, scrivere nome nella causale"
                          value={formData.payment_notes}
                          onChange={(e) => setFormData({ ...formData, payment_notes: e.target.value })}
                          data-testid="match-payment-notes-input"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Visibile a tutti i membri della lega nella scheda partita.
                      </p>
                    </div>

                    <div className="flex items-start justify-between gap-3 p-4 rounded-lg bg-muted">
                      <div className="min-w-0">
                        <Label htmlFor="is_public" className="font-medium flex items-center gap-2">
                          {formData.is_public ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                          Partita Pubblica
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          {formData.is_public ? 'Visibile a tutti nella ricerca' : 'Solo su invito'}
                        </p>
                      </div>
                      <Switch
                        className="shrink-0"
                        id="is_public"
                        checked={formData.is_public}
                        onCheckedChange={(v) => setFormData({ ...formData, is_public: v })}
                        data-testid="match-public-switch"
                      />
                    </div>

                    <div className="flex items-start justify-between gap-3 p-4 rounded-lg bg-muted">
                      <div className="min-w-0">
                        <Label htmlFor="goalkeeper_rotation_enabled" className="font-medium">
                          🧤 Rotazione portiere ogni 5 min
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Quando attivo, tutti i giocatori ruoteranno in porta a turno.
                        </p>
                      </div>
                      <Switch
                        className="shrink-0"
                        id="goalkeeper_rotation_enabled"
                        checked={!!formData.goalkeeper_rotation_enabled}
                        onCheckedChange={(v) => setFormData({ ...formData, goalkeeper_rotation_enabled: v })}
                        data-testid="match-goalkeeper-rotation-switch"
                      />
                    </div>

                    {!isEditing && formData.league_id && (
                      <div className="flex items-start justify-between gap-3 p-4 rounded-lg bg-muted">
                        <div className="min-w-0">
                          <Label htmlFor="admins_confirmed" className="font-medium">
                            👑 Admin della lega già titolari
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Gli admin entrano subito come titolari, anche nelle partite ripetute.
                          </p>
                        </div>
                        <Switch
                          className="shrink-0"
                          id="admins_confirmed"
                          checked={adminsConfirmed}
                          onCheckedChange={setAdminsConfirmed}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* ============================================================ */}
                {/* Navigation                                                   */}
                {/* ============================================================ */}
                <div className="flex gap-2 sm:gap-3 pt-4 border-t flex-wrap">
                  {step > 1 ? (
                    <Button
                      type="button" variant="outline"
                      onClick={() => setStep((s) => s - 1)}
                      className="flex-1 sm:flex-initial min-w-0"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Indietro
                    </Button>
                  ) : (
                    <Button
                      type="button" variant="outline"
                      onClick={() => navigate(-1)}
                      className="flex-1 sm:flex-initial min-w-0"
                    >
                      Annulla
                    </Button>
                  )}

                  {step < 3 ? (
                    <Button
                      key="next-btn"
                      type="button"
                      onClick={goNext}
                      className="flex-1 min-w-0 bg-gradient-to-br from-blue-500 to-indigo-600 hover:opacity-90"
                    >
                      Avanti
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      key="submit-btn"
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading}
                      className="flex-1 min-w-0 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:opacity-90"
                      data-testid="submit-match-btn"
                    >
                      {loading ? (isEditing ? 'Salvataggio…' : 'Creazione…') : (
                        isEditing ? 'Salva Modifiche'
                          : 'Crea Partita'
                      )}
                      <Check className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Preview sidebar */}
        <aside className="lg:col-span-1 min-w-0">
          <div className="lg:sticky lg:top-6">
            <PreviewCard
              formData={formData}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              selectedPlace={selectedPlace}
              league={currentLeague}
              competitionName={selectedCompetition?.name}
              repeatCount={repeat.enabled ? (repeat.times ? repeat.times + 1 : Infinity) : 1}
            />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CreateMatchPage;
